import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import Modal from "react-responsive-modal";
import Eye from "react-feather/dist/icons/eye";
import AlertCircle from "react-feather/dist/icons/alert-circle";

import style from "./style.module.scss";
import WizardFooter from "../wizard-footer";
import PageContent from "../page-content";
import { getProductType } from "../../../product";
import { requireLogin } from "../../../security";
import { productQueryOptions } from "../../../data/queries/products";
import { useSaveProductMutation } from "../../../data/queries/products";
import { useUser } from "../../../data/context/auth-context";
import { Button, Spinner } from "../../../ui";
import { trackCustomizationComplete } from "../../../analytics";

const productLabels: Record<string, { title: string; description: string }> = {
  "photo-book": {
    title: "Ajoutez des photos à votre livre photo",
    description:
      "Cliquez sur les différentes pages pour ajouter une photo ou glissez/déposez vos photos.",
  },
  calendar: {
    title: "Personnalisez votre calendrier",
    description: "Définissez la couverture et les pages de votre calendrier.",
  },
};

function Customize() {
  const { uid } = useParams();
  const user = useUser();
  const { data: product, isLoading } = useQuery(productQueryOptions(uid || ""));
  const saveMutation = useSaveProductMutation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);

  const setOptions = (newOptions: Record<string, unknown>) => {
    if (!product || !user) return;

    saveMutation.mutate({
      product: {
        ...product,
        options: {
          ...product.options,
          ...newOptions,
        },
      },
      userId: user.uid,
    });
  };

  if (isLoading || !product) {
    return <Spinner />;
  }

  const productType = getProductType(product.name);
  if (!productType) {
    return <Spinner />;
  }

  // Cast to any since product.options is Record<string, any> from API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Edit = productType.edit as React.ComponentType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Preview = productType.preview as React.ComponentType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isReady = productType.isReady(product.options as any);
  const labels = productLabels[product.name] || {
    title: "Personnalisez votre produit",
    description: "Définissez les options de votre produit.",
  };

  const handleContinueClick = () => {
    const pageCount = Object.keys(product.options?.pages || {}).length;
    trackCustomizationComplete(product.name, pageCount);
  };

  return (
    <>
      <PageContent title={labels.title} description={labels.description}>
        <Edit
          productId={product.uid}
          userId={user?.uid || ""}
          options={product.options}
          setOptions={setOptions}
        />
      </PageContent>
      <WizardFooter uid={uid} step={3} enabledSteps={isReady ? [1, 2, 3, 4] : [1, 2, 3]}>
        {!isReady && (
          <button
            type="button"
            className={style.warning}
            onClick={() => setWarningOpen(true)}
            data-tooltip="Photos manquantes"
          >
            <AlertCircle />
          </button>
        )}
        <Button
          className={style.preview}
          title="Aperçu du résultat"
          onClick={() => setPreviewOpen(true)}
          isBorderless
        >
          <Eye />
        </Button>
        {isReady ? (
          <Link to={`/shipping/${product.uid}`} onClick={handleContinueClick}>
            <Button isPrimary>Continuer</Button>
          </Link>
        ) : (
          <Button isPrimary isDisabled>
            Continuer
          </Button>
        )}
      </WizardFooter>
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} center>
        <h3 className={style.previewTitle}>Aperçu du résultat</h3>
        <Preview options={product.options} />
      </Modal>
      <Modal
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        center
        classNames={{ modal: style.warningModal }}
      >
        <h3 className={style.warningTitle}>Photos manquantes</h3>
        <p className={style.warningText}>
          Veuillez remplir les photos pour chaque page de votre produit avant de passer commande.
        </p>
        <p className={style.warningText}>
          Chaque emplacement de photo doit contenir une image pour que votre commande puisse être
          traitée.
        </p>
        <Button isPrimary onClick={() => setWarningOpen(false)}>
          Continuer
        </Button>
      </Modal>
    </>
  );
}

export default requireLogin()(Customize);
